import Axios from "axios";

import * as apiFactory from "./ApiFactory";

import { Api } from "./Api";

import { readBlobAsText } from "@/services/UtilitiesService";

import { useBrandStore } from "@/stores/brand-store";
import { useSettingsStore } from "@/stores/settings-store";

import type { AxiosResponse } from "axios";
import type { Multipart } from "@/models/Multipart";
import type { Transcript } from "@/stores/case-file-store";
import type UnPasswordRequest from "@/models/UnPasswordRequest";
import type UnPasswordResult from "@/models/UnPasswordResult";
import type { ViewingSession } from "@/models/ViewingSession";
import type { PageText, SearchRequest, SearchResult } from "@/models/Viewer";

export async function requestTranscriptRender(
  clientName: string,
  brand: string,
  caseReference: string,
  fileReference: string,
): Promise<void> {
  const client = await apiFactory.WithBearer();
  await client.post(
    `api/binary/renderTranscript/${clientName}/${brand}/${encodeURIComponent(caseReference)}/${fileReference}`,
  );
}

export async function get<NoRedirect extends boolean>(
  clientName: string,
  brand: string,
  caseReference: string,
  fileReference: string,
  noRedirect?: NoRedirect,
): Promise<NoRedirect extends true ? string : object> {
  const client = await apiFactory.WithBearer();

  if (noRedirect) {
    return await client.get(
      `api/binary/${clientName}/${brand}/${encodeURIComponent(caseReference)}/${fileReference}?redirect=false`,
    );
  }

  return await client.get(`api/binary/${clientName}/${brand}/${encodeURIComponent(caseReference)}/${fileReference}`);
}

export async function saveForm(
  clientName: string,
  brand: string,
  caseReference: string,
  fileReference: string,
  formFileReference: string,
  data: string,
): Promise<void> {
  const client = await apiFactory.WithBearer();

  const formUri = await client.post<{}, string>(
    `api/binary/${clientName}/${brand}/${encodeURIComponent(caseReference)}/${formFileReference}?redirect=false`,
  );

  try {
    const arrayBuffer = await new Blob([data], {
      type: "application/json",
    }).arrayBuffer();
    // Put to the URI returned
    const axiosResponse: AxiosResponse = await Axios.create().put(formUri, arrayBuffer, {
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "content-type": "application/octet-stream",
      },
    });

    if (axiosResponse.status !== 201) {
      throw "Uri did not accept the put request";
    }
    // Notify the API that our upload has completed
    await client.post(["api/recordaction/formedit", clientName, brand, caseReference, fileReference], {
      formFileReference,
      formUri,
    });
  } catch (ex) {}
}

export async function getFormDefinition(
  clientName: string,
  brand: string,
  formType: string,
  formId: string,
  version: string,
) {
  const client = await apiFactory.WithBearer();
  return await client.get(`api/formdefinition/${clientName}/${brand}/${formType}/${formId}/${version}`);
}

export async function getOriginal(
  clientName: string,
  brand: string,
  caseReference: string,
  fileReference: string,
): Promise<object> {
  const client = await apiFactory.WithBearer();
  return await client.get<object>(
    `api/binary/original/${clientName}/${brand}/${encodeURIComponent(caseReference)}/${fileReference}`,
  );
}

export async function verifySHA256Hash(
  clientName: string,
  brand: string,
  caseReference: string,
  fileReference: string,
  hash: string,
): Promise<SHA256MatchedFile[]> {
  const client = await apiFactory.WithBearer();
  return await client.post<{ hash: string }, SHA256MatchedFile[]>(
    ["api/recordaction/verify-hash", clientName, brand, caseReference, fileReference],
    { hash },
  );
}

export async function getSupportingType<
  T extends "peak" | "transcript",
  R extends T extends "peak" ? number[] : Transcript,
>(clientName: string, brand: string, caseReference: string, fileReference: string, supportingType: T) {
  const client = await apiFactory.WithBearer();

  let sasURI: string;

  try {
    sasURI = await client.get<string>(
      `api/binary/supporting/${clientName}/${brand}/${encodeURIComponent(caseReference)}/${fileReference}/${supportingType}?redirect=false`,
    );
  } catch (ex) {
    throw ex as { status: number };
  }

  const transcriptRaw = await fetch(sasURI);
  const blob = await transcriptRaw.blob();

  return JSON.parse(await readBlobAsText(blob)) as R;
}

export async function getSnapshot(
  clientName: string,
  brand: string,
  caseReference: string,
  fileReference: string,
  revision: number | undefined,
) {
  const client = await apiFactory.WithBearer();

  const params = [
    ["revision", revision],
    ["redirect", false],
  ]
    .filter(([val]) => val != null)
    .map((arr) => arr.join("="))
    .join("&");

  return await client.get<string>(
    `api/binary/supporting/${clientName}/${brand}/${encodeURIComponent(caseReference)}/${fileReference}/screenshot?${params.toString()}`,
  );
}

export async function saveSnapshot(
  clientName: string,
  brand: string,
  caseReference: string,
  caseFile: string,
  fileReference: string,
  formData: Multipart[],
): Promise<void> {
  const client = await apiFactory.WithBearer();
  await client.post<Multipart[], void>(
    `api/binary/supportingtype/${clientName}/${brand}/${encodeURIComponent(caseReference)}/${caseFile}/${fileReference}/screenshot`,
    formData,
    { "Content-Type": "multipart/formdata" },
    true,
  );
}

export async function unPassword(
  clientName: string,
  brand: string,
  caseReference: string,
  fileReference: string,
  payload: UnPasswordRequest,
): Promise<UnPasswordResult> {
  const client: Api = await apiFactory.WithBearer();
  return await client.post<UnPasswordRequest, UnPasswordResult>(
    ["api/recordaction/unpassword", clientName, brand, caseReference, fileReference],
    payload,
  );
}

export async function preConvert(
  clientName: string,
  brand: string,
  caseReference: string,
  fileReference: string,
): Promise<void> {
  const client: Api = await apiFactory.WithBearer();
  await client.post(["api/recordAction/preconvert", clientName, brand, caseReference, fileReference]);
}

export async function getDocumentSVG(session: string, pageNumber: number) {
  const p = pageNumber;
  const client: Api = await apiFactory.WithoutBearer(true);
  switch (useBrandStore().apiVersion) {
    case "v1":
      return client.get<string>(
        `${useBrandStore().selectedClient}/Page/q/${p}?DocumentID=u${session}&Scale=1&ContentType=svg`,
        { Accept: "*/*" },
      );
    case "v2":
      return client.get<string>(`Page/q/${p + 1}?DocumentID=${session}&Scale=1&Content=svgb`, { Accept: "*/*" });
  }
}

export async function getDocumentThumbnail(session: string, pageNumber: number) {
  const client: Api = await apiFactory.WithoutBearer(true);
  return client.get<string>(getDocumentImageUrl(session, pageNumber), { Accept: "*/*" }, "arraybuffer");
}

export function getDocumentImageUrl(session: string, pageNumber: number) {
  switch (useBrandStore().apiVersion) {
    case "v1":
      return `${useBrandStore().selectedClient}/Page/q/${pageNumber}?DocumentID=u${session}&Scale=${
        useSettingsStore().scaleFactor || 1
      }&Quality=85&ContentType=jpeg`;
    case "v2":
      return `Page/q/${pageNumber + 1}?DocumentID=${session}&Scale=${useSettingsStore().scaleFactor || 1}&Content=jpeg`;
  }
}

export async function getDocumentText(session: string, pageNumber: number): Promise<{ pages: PageText[] }> {
  const p = pageNumber;
  const client: Api = await apiFactory.WithoutBearer(true);
  switch (useBrandStore().apiVersion) {
    case "v1":
      return client.get<{ pages: PageText[] }>(
        `${useBrandStore().selectedClient}/Document/q/${p}-${p}/Text?DocumentID=u${session}`,
      );
    case "v2": {
      const result = await client.get<{ pages: PageText[] }>(`Document/q/${p + 1}-${p + 1}/Text?DocumentID=${session}`);

      // Fix broken text layer property
      result.pages.forEach((pg) => {
        const item2 = (pg as unknown as { Item2: string }).Item2;
        if (!item2) return;
        pg.text = item2;
      });

      return result;
    }
  }
}

export async function getDocumentAttributes(session: string) {
  const client: Api = await apiFactory.WithoutBearer(true);
  switch (useBrandStore().apiVersion) {
    case "v1":
      return await client.get<{ pageCount: number }>(
        `${useBrandStore().selectedClient}/Document/q/Attributes?DocumentID=u${session}&DesiredPageCountConfidence=50`,
      );
    case "v2":
      return await client.get<{ pageCount: number }>(`Document/q/Attributes?DocumentID=${session}`);
  }
}

export async function createSession(payload: CreateSessionPayload): Promise<ViewingSession> {
  const api: Api = await apiFactory.WithBearer();
  const { client, brand, casename, casefile, pageNumber, isOnDemand } = payload;
  return api.get<ViewingSession>(
    `api/binary/view/${client}/${brand}/${encodeURIComponent(casename)}/${casefile}/${isOnDemand}/${pageNumber}?sessionStartOption=${payload.createNew === true ? "requiresNew" : "joinExisting"}`,
  );
}

export async function searchDocument(sessionId: string, payload: SearchRequest) {
  const api: Api = await apiFactory.WithoutBearer(true);
  switch (useBrandStore().apiVersion) {
    case "v1":
      return api.post<{ input: SearchRequest }, any>(
        `${useBrandStore().selectedClient}/v2/viewingSessions/${sessionId}/searchTasks`,
        { input: payload },
      );
    case "v2":
      return api.post<{ input: SearchRequest }, any>(`v2/viewingSessions/${sessionId}/searchTasks`, { input: payload });
  }
}

export async function getSearchResults(processId: string, continueToken?: string): Promise<SearchResponse> {
  const api: Api = await apiFactory.WithoutBearer(true);
  switch (useBrandStore().apiVersion) {
    case "v1":
      return api.get<SearchResponse>(
        `${useBrandStore().selectedClient}/v2/searchTasks/${processId}/results${
          continueToken ? "?continueToken=" + continueToken : ""
        }`,
      );
    case "v2": {
      const response = await api.get<{ results: any[] }>(
        `v2/searchTasks/${processId}${continueToken ? "?continueToken=" + continueToken : ""}`,
      );
      return {
        ...response,
        results: response.results.map((r: any) => ({
          ...r.result,
          text: r.result.txt,
          pageIndex: r.result.pageNumber - 1,
        })),
      };
    }
  }
}

export function getSvgDimensions(svg: string): {
  width: number;
  height: number;
} {
  const parser = new DOMParser();
  const svgElement = parser.parseFromString(svg, "image/svg+xml");
  const image = svgElement.getElementsByTagName("image")[0];

  switch (useBrandStore().apiVersion) {
    case "v1":
      if (image.ownerSVGElement!.width.animVal.value < 1) {
        return {
          width: image.width.animVal.value,
          height: image.height.animVal.value,
        };
      }
      return {
        width: image.ownerSVGElement!.width.animVal.value,
        height: image.ownerSVGElement!.height.animVal.value,
      };
    case "v2":
      return {
        width: image.width.animVal.value,
        height: image.height.animVal.value,
      };
  }
}

interface CreateSessionPayload {
  client: string;
  brand: string;
  casename: string;
  casefile: string;
  pageNumber: number;
  isOnDemand: boolean;
  createNew?: boolean;
}

export interface SearchResponse {
  results: SearchResult[];
  continueAfter?: number;
  continueToken?: string;
}

export interface SHA256MatchedFile {
  clientName: string;
  brand: string;
  caseReference: string;
  hash: string;
  created: string /* Parsable to Date */;
  firstKnownAs: string;
  metadata: { operation: string | undefined };
  matched: boolean;
}
